// Tilted Asset Allocation — donut visualisation tied to the AFML
// Cycle Stability Forecast.
//
// This card is the visual companion to the "Cycle Stability Forecast"
// gauge on the same dashboard: where that widget answers *"is the
// regime about to flip?"*, this one answers *"and what allocation does
// the current cycle imply?"*. We reuse the ``AllocationDonut`` from
// the risk-assessment feature so the visual language stays identical
// to the *Recommended Allocation* chart users already see on
// ``/dashboard/risk-assessment/test?view=results`` — same palette,
// same hover-to-isolate interaction, same legend rhythm.
//
// Data path: ``/api/v1/macro/regime-allocation`` returns
//   * ``current_regime``     — auto-detected regime label
//   * ``baseline_allocation``— 7-bucket "neutral" allocation
//   * ``tilted_allocation``  — same 7 buckets, regime-tilted
//   * ``tilt_rationale``     — one-paragraph plain-English summary
//
// We render ``tilted_allocation`` exclusively here (the bar chart
// inside ``RegimeTilt`` already lets the user compare the two).
//
// Empty / loading / error states mirror the rest of the macro feature
// so the dashboard layout never jumps regardless of backend state.

import { useEffect, useState } from 'react';

import {
    formatMonthEnd,
    macroApi,
    type MacroRegimeState,
    type RegimeAllocation,
} from '../../../api/macro';
import { AllocationDonut } from '../../risk-assessment/components/AllocationDonut';

// Map the backend's snake_case asset keys to the human-readable labels
// used throughout the rest of the UI (matches RegimeTilt's ASSET_ORDER
// and the risk-assessment Recommended Allocation labels). Keeping this
// table colocated with the consumer avoids leaking presentation
// concerns into the API client.
const ASSET_LABELS: Record<string, string> = {
    equities: 'Equities',
    etf: 'ETFs',
    commodities: 'Commodities',
    reits: 'REITs',
    cryptocurrencies: 'Crypto',
    fx: 'FX',
    cash: 'Cash',
};

// Preserve a stable display order so the donut's slice colours don't
// reshuffle between renders just because the backend's JSON key order
// changed. Order matches RegimeTilt's ASSET_ORDER so the bar chart
// above and the donut below read in the same sequence.
const DISPLAY_ORDER = [
    'equities',
    'etf',
    'commodities',
    'reits',
    'cryptocurrencies',
    'fx',
    'cash',
];

function relabelAndOrder(
    alloc: Record<string, number>,
): Record<string, number> {
    const out: Record<string, number> = {};
    for (const key of DISPLAY_ORDER) {
        if (alloc[key] !== undefined) {
            out[ASSET_LABELS[key] ?? key] = alloc[key];
        }
    }
    // Trailing fallthrough — surface any unexpected keys the backend
    // adds in the future so a new asset class never silently disappears
    // from the chart.
    for (const [key, val] of Object.entries(alloc)) {
        if (!DISPLAY_ORDER.includes(key)) {
            out[ASSET_LABELS[key] ?? key] = val;
        }
    }
    return out;
}

// Map each human-readable asset-class label (as produced by
// ``relabelAndOrder``) to its dedicated ranking page. Clicking a legend
// row on the tilted-allocation donut deep-links the user straight into
// the corresponding asset-class leaderboard, tightening the loop between
// the macro-driven allocation view and the per-asset intelligence pages.
// Cash has no ranking page, so it is intentionally omitted (stays static).
const RANKING_ROUTES: Record<string, string> = {
    Equities: '/dashboard/stock-ranking',
    ETFs: '/dashboard/etf-ranking',
    Commodities: '/dashboard/commodities-ranking',
    REITs: '/dashboard/reits-ranking',
    Crypto: '/dashboard/crypto-ranking',
    FX: '/dashboard/fx-ranking',
};

const REGIME_ACCENT: Record<string, string> = {
    Expansion: '#10b981',   // emerald-500
    Recovery: '#3b82f6',    // blue-500
    Slowdown: '#f59e0b',    // amber-500
    Contraction: '#ef4444', // red-500
};

export const TiltedAllocationCard = () => {
    const [allocation, setAllocation] = useState<RegimeAllocation | null>(null);
    const [regimeState, setRegimeState] = useState<MacroRegimeState | null>(
        null,
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [state, alloc] = await Promise.all([
                    macroApi.getCurrentRegime(),
                    macroApi.getRegimeAllocation(),
                ]);
                if (cancelled) return;
                setRegimeState(state);
                setAllocation(alloc);
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err instanceof Error ? err.message : 'Failed to load.',
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    if (loading) {
        return (
            <div className="surface-card-skeleton h-80" aria-busy>
                <p className="muted text-sm">Loading tilted allocation…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="surface-card-error">
                <p className="font-bold">Tilted Allocation unavailable</p>
                <p className="text-sm mt-1">{error}</p>
            </div>
        );
    }

    if (!allocation) {
        return (
            <div className="surface-card">
                <h3 className="section-title">
                    Tilted Asset Allocation Based on Cycle Stability Forecast
                </h3>
                <p className="muted text-sm mt-2">
                    No tilted allocation to display yet — the macro
                    pipeline has not produced its first classification.
                </p>
            </div>
        );
    }

    const tiltedDisplay = relabelAndOrder(allocation.tilted_allocation);
    const horizon = regimeState?.afml_prediction?.horizon_months ?? 3;
    const transitionProb =
        regimeState?.afml_prediction?.transition_probability != null
            ? regimeState.afml_prediction.transition_probability * 100
            : null;
    const accent =
        REGIME_ACCENT[allocation.current_regime] ?? '#10b981';

    return (
        <div className="surface-card">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="section-title">
                        Tilted Asset Allocation Based on Cycle Stability
                        Forecast
                    </h3>
                    <p className="muted text-xs mt-1">
                        Auto-tuned for the{' '}
                        <span
                            className="font-bold"
                            style={{ color: accent }}
                        >
                            {allocation.current_regime}
                        </span>{' '}
                        regime · {horizon}-month horizon
                        {regimeState?.date
                            ? ` · ${formatMonthEnd(regimeState.date)}`
                            : ''}
                    </p>
                </div>
                {transitionProb !== null && (
                    <div className="text-right">
                        <p className="muted text-[11px] uppercase tracking-wider font-semibold">
                            Transition Risk
                        </p>
                        <p
                            className="text-2xl font-black tabular-nums leading-none mt-1"
                            style={{ color: accent }}
                        >
                            {transitionProb.toFixed(1)}%
                        </p>
                    </div>
                )}
            </div>

            <p className="text-sm muted mt-3 max-w-3xl leading-relaxed">
                Recommended weights derived from the AFML meta-classifier's
                current regime read and {horizon}-month transition
                probability. Hover a slice to see the exact percentage, or
                click an asset class in the legend to open its ranking page.
            </p>

            <div className="mt-6">
                <AllocationDonut
                    allocation={tiltedDisplay}
                    size={260}
                    linkFor={(label) => RANKING_ROUTES[label]}
                />
            </div>

            {allocation.tilt_rationale && (
                <p className="mt-6 text-sm text-slate-600 dark:text-slate-400 leading-relaxed border-l-4 pl-4 py-1"
                   style={{ borderColor: accent }}>
                    {allocation.tilt_rationale}
                </p>
            )}
        </div>
    );
};

export default TiltedAllocationCard;
